import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  getTaskKindConfig,
  FALLBACK_KIND_CONFIG,
} from '../config/task-kind-registry'
import { getHandler, type HandlerContext } from '../config/action-handlers'
import {
  insertManualTask as insertManualTaskMutation,
  rescheduleTask,
  dismissTask,
} from '../services/mutations'
import {
  ASSISTANT_TASKS_QUERY_KEY,
  useInvalidateAssistantTasks,
} from './useAssistantTasks'
import type { Task } from '../schemas/task'
import type { ManualTaskFormData } from '../schemas/manual-task-form'

const warnedKinds = new Set<string>()

export type ManualFormPrefill = Partial<Task> | undefined

export const useTaskActions = (opts?: {
  openManualForm?: (prefill: ManualFormPrefill) => void
}) => {
  const navigate = useNavigate()
  const invalidate = useInvalidateAssistantTasks()
  const queryClient = useQueryClient()
  const [pendingId, setPendingId] = useState<string | null>(null)

  const ctx: HandlerContext = {
    navigate,
    openManualForm: (prefill) => opts?.openManualForm?.(prefill),
  }

  // Row-click / primary-button handler. Auto tasks route to their kind handler
  // (e.g. openProject); manual tasks route to markDone (complete). There is
  // no reactivation path — dismissed tasks are never rendered.
  const runPrimary = useCallback(
    async (task: Task) => {
      const config = getTaskKindConfig(task.kind)
      if (config === FALLBACK_KIND_CONFIG && !warnedKinds.has(task.kind)) {
        warnedKinds.add(task.kind)
        // eslint-disable-next-line no-console
        console.warn(`[assistant] Unknown task kind "${task.kind}" — rendering fallback card`)
      }
      if (config.handlerId === '__noop__') return

      setPendingId(task.id)
      try {
        const handler = getHandler(config.handlerId)
        await handler(task, ctx)
        await invalidate()
      } finally {
        setPendingId(null)
      }
    },
    [invalidate, navigate]
  )

  // Reschedule — per-task and bulk. Writes target_time only; the task
  // stays visible and its bucket recomputes from the new deadline, which
  // moves it between the Urgent / Cette semaine / En attente tabs.
  const reschedule = useCallback(
    async (task: Task, chosenDate: Date) => {
      setPendingId(task.id)
      try {
        await rescheduleTask({ id: task.id, chosenDate: chosenDate.toISOString() })
        await invalidate()
      } finally {
        setPendingId(null)
      }
    },
    [invalidate],
  )

  const ignore = useCallback(
    async (task: Task) => {
      setPendingId(task.id)
      try {
        await dismissTask(task.id)
        await invalidate()
      } finally {
        setPendingId(null)
      }
    },
    [invalidate]
  )

  const createManual = useCallback(
    async (input: ManualTaskFormData) => {
      const inserted = await insertManualTaskMutation(input)
      queryClient.setQueryData<Task[]>(ASSISTANT_TASKS_QUERY_KEY, (prev) => {
        const existing = prev ?? []
        if (existing.some((t) => t.id === inserted.id)) return existing
        return [inserted, ...existing]
      })
      await queryClient.refetchQueries({ queryKey: ASSISTANT_TASKS_QUERY_KEY })
    },
    [queryClient],
  )

  return { runPrimary, reschedule, ignore, createManual, pendingId }
}
